/* ─────────────────────────────────────────────────────────────────────────
 * About — everything laid out on the desk beside the mat.
 *
 * The page is the same surface as the section: one cutting board, paper laid
 * on it, annotations in pencil. So the content is shaped the way the section's
 * own data is — a frame is a photograph WITH a caption, a step is a title with
 * one line under it, and nothing here is a paragraph pretending to be a
 * headline. The photographs are pulled from the same roll the section grades,
 * so the About page cannot drift from the work it is describing.
 * ───────────────────────────────────────────────────────────────────────── */

export interface SheetFrame {
    /** File under the section's own image base. */
    src: string
    title: string
    place: string
    year: string
    /** Grease-pencil select — ringed on the strip. Two, never more. */
    select?: boolean
}

export interface Step {
    n: string
    name: string
    body: string
}

export interface Fact {
    k: string
    v: string
}

/** The portrait at the top of the sheet. From the Rome roll — it is the only
 *  frame on the site she is actually in. */
export const PORTRAIT = {
    src: 'cinematic-05.jpg',
    caption: 'MAREN, MID-SHOOT — ROME, 2024'
}

/** The frame beside the statement. Copenhagen, one window, no flash. */
export const STATEMENT_FRAME = {
    src: 'soft-03.jpg',
    caption: 'LOW FLAME — COPENHAGEN, 2024'
}

/** Six frames off the strip, one from each body of work she keeps returning
 *  to. Same titles, places and years as the section's own catalogue. */
export const SHEET: SheetFrame[] = [
    { src: 'raw-01.jpg', title: 'Wet Street, 6am', place: 'New York', year: '2023', select: true },
    { src: 'bold-01.jpg', title: 'Verde on Red Rock', place: 'Almería', year: '2025' },
    { src: 'soft-05.jpg', title: 'Steam, Green Glass', place: 'Studio A', year: '2024' },
    { src: 'dreamy-01.jpg', title: 'Lavender, Long Exposure', place: 'Provence', year: '2024', select: true },
    { src: 'cinematic-07.jpg', title: 'Last Light', place: 'Hong Kong', year: '2023' },
    { src: 'raw-06.jpg', title: 'Rain, Shibuya', place: 'Tokyo', year: '2023' }
]

/** How a job actually runs. Four cards, because a shoot has four moments and
 *  padding it to six would be a brochure. */
export const STEPS: Step[] = [
    {
        n: '01',
        name: 'The call',
        body: "Twenty minutes, no mood board. You tell me what the photographs are for and what happens if they are wrong. I ask more questions than you expect about the room."
    },
    {
        n: '02',
        name: 'The cut',
        body: "I choose the look and then prove it: three test frames, graded through the exact stock the job will run on. If the look is wrong, it is wrong on my time, not on yours."
    },
    {
        n: '03',
        name: 'The shoot',
        body: "One or two days. Available light wherever it will hold, one lamp and a bounce where it will not, and a hard stop when the light goes rather than an hour of pushing it."
    },
    {
        n: '04',
        name: 'The print',
        body: "Every frame graded through one grade, so the set reads as one roll. Selects inside a week, the full take in three, and the files are yours outright."
    }
]

/** Pinned to the corkboard. The things people email to ask before they book. */
export const FACTS: Fact[] = [
    { k: 'Based', v: 'Lisbon — works anywhere with a runway' },
    { k: 'Booking', v: 'Six to eight weeks out, usually' },
    { k: 'Days', v: 'Two-day minimum once a flight is involved' },
    { k: 'Crew', v: 'Alone, or with one assistant on the big rooms' },
    { k: 'Rights', v: 'Full usage, no expiry, no renewal invoice' },
    { k: 'Turnaround', v: 'Selects in a week, the roll in three' }
]

/** Twelve years, in the four numbers that are actually true. */
export const NUMBERS: Fact[] = [
    { k: 'Years behind a camera', v: '12' },
    { k: 'Cities on the roll', v: '31' },
    { k: 'Looks on the mat', v: '05' },
    { k: 'Frames kept per hundred', v: '04' }
]

/** The long version, in her own voice. Split into paragraphs so the sheet can
 *  stagger them in the way the letters stagger out of the card. */
export const STATEMENT: string[] = [
    "I started in a darkroom at seventeen because it was the warmest room in the building, and I stayed because it was the only place where a decision could not be undone. That is still how I work. The look is chosen before the shutter, not found afterwards in a slider.",
    "Most of what I photograph is ordinary: a kitchen after service, two people who have run out of things to say, a wall that happens to be the right red. The job is not to make those things beautiful. It is to decide how they should feel, commit to it, and hold that decision across every frame so the set arrives as one body of work rather than a folder.",
    "Five looks live on the mat next door. They are not filters. Each one is a film stock, a lighting habit and a set of things I refuse to do, and picking one is the first real decision of any job we do together."
]

export const SIGNATURE = 'Maren'
